import { ShoppingCart, Minus, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import type { CartItem, ComboCartItem } from "@/hooks/usePublicCart";

interface PublicCartProps {
  items: CartItem[];
  comboItems?: ComboCartItem[];
  subtotal: number;
  itemCount: number;
  deliveryFee?: number;
  onUpdateQuantity: (index: number, quantity: number) => void;
  onRemove: (index: number) => void;
  onUpdateComboQuantity?: (index: number, quantity: number) => void;
  onRemoveCombo?: (index: number) => void;
  onCheckout: () => void;
  disabled?: boolean;
}

export function PublicCart({
  items,
  comboItems = [],
  subtotal,
  itemCount,
  deliveryFee = 0,
  onUpdateQuantity,
  onRemove,
  onUpdateComboQuantity,
  onRemoveCombo,
  onCheckout,
  disabled,
}: PublicCartProps) {
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(price);
  };

  const total = subtotal + deliveryFee;
  const hasItems = items.length > 0 || comboItems.length > 0;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button 
          className="fixed bottom-4 right-4 h-14 px-6 shadow-lg z-50"
          disabled={itemCount === 0}
        >
          <ShoppingCart className="h-5 w-5 mr-2" />
          <span className="mr-2">Ver Carrinho</span>
          {itemCount > 0 && (
            <Badge variant="secondary" className="ml-1">
              {itemCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle>Seu Carrinho</SheetTitle>
        </SheetHeader>

        {!hasItems ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            Seu carrinho está vazio
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto py-4 space-y-3">
              {/* Regular items */}
              {items.map((item, index) => (
                <div key={`item-${item.id}-${index}`} className="flex gap-3 p-3 bg-muted/50 rounded-lg">
                  {item.image_url && (
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="w-16 h-16 object-cover rounded"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <h4 className="font-medium text-sm line-clamp-1">{item.name}</h4>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 -mt-1 -mr-1"
                        onClick={() => onRemove(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    {item.selectedAddons && item.selectedAddons.length > 0 && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {item.selectedAddons.map(a => `+ ${a.name}`).join(", ")}
                      </div>
                    )}
                    {item.notes && (
                      <p className="text-xs text-muted-foreground mt-0.5 italic">{item.notes}</p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => onUpdateQuantity(index, item.quantity - 1)}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-6 text-center text-sm">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => onUpdateQuantity(index, item.quantity + 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                      <span className="font-medium text-sm">
                        {formatPrice(item.price * item.quantity)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              {/* Combo items */}
              {comboItems.map((combo, index) => (
                <div key={`combo-${combo.comboId}-${index}`} className="flex gap-3 p-3 bg-muted/50 rounded-lg">
                  {combo.image_url && (
                    <img
                      src={combo.image_url}
                      alt={combo.comboName}
                      className="w-16 h-16 object-cover rounded"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium text-sm line-clamp-1">{combo.comboName}</h4>
                        <Badge variant="secondary" className="text-xs mt-0.5">Combo</Badge>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 -mt-1 -mr-1"
                        onClick={() => onRemoveCombo?.(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    {/* Show selected items in combo */}
                    {combo.selection.slotSelections && Object.keys(combo.selection.slotSelections).length > 0 && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {Object.values(combo.selection.slotSelections)
                          .flat()
                          .map((s: any) => s.productName)
                          .join(", ")}
                      </div>
                    )}
                    {combo.notes && (
                      <p className="text-xs text-muted-foreground mt-0.5 italic">{combo.notes}</p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => onUpdateComboQuantity?.(index, combo.quantity - 1)}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-6 text-center text-sm">{combo.quantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => onUpdateComboQuantity?.(index, combo.quantity + 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                      <span className="font-medium text-sm">
                        {formatPrice(combo.price * combo.quantity)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t pt-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              {deliveryFee > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Taxa de entrega</span>
                  <span>{formatPrice(deliveryFee)}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between font-semibold">
                <span>Total</span>
                <span className="text-primary">{formatPrice(total)}</span>
              </div>
              <Button 
                className="w-full" 
                size="lg"
                onClick={onCheckout}
                disabled={disabled}
              >
                Finalizar Pedido
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}